import * as React from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {css} from "emotion";
import {NotificationLevel} from "@components/version-update/VersionUpdate";

const iconStyle = css`
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;
    display: block;
    font-size: 64px;
`;

const messageStyle = css`
    text-align: center;
    font-size: 15px;
    line-height: 1.6;
    color: #333;
`;

const versionInfoStyle = css`
    background: #f5f5f5;
    padding: 12px;
    border-radius: 6px;
    margin: 16px 0;
    font-family: monospace;
    font-size: 13px;
`;

const whatsNewStyle = css`
    text-align: left;
    background: #f5f7ff;
    border: 1px solid #e0e5ff;
    padding: 12px 16px;
    border-radius: 6px;
    margin: 16px 0;
    max-height: 220px;
    overflow-y: auto;
`;

const whatsNewTitleStyle = css`
    font-size: 13px;
    font-weight: 600;
    color: #4451b8;
    margin: 0 0 8px;
`;

const whatsNewGroupStyle = css`
    &:not(:first-child) {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #e6eaff;
    }
`;

const whatsNewVersionStyle = css`
    font-size: 11px;
    font-weight: 600;
    color: #7a83c4;
    margin: 0 0 3px;
    font-family: monospace;
`;

const whatsNewListStyle = css`
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.6;
    color: #333;

    li {
        margin-bottom: 4px;
    }
`;

const buttonStyle = css`
    background-color: #667eea;
    border: none;
    border-radius: 24.5px;
    color: white;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    height: 40px;
    outline: none;
    padding: 0 32px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #5568d3;
    }

    &:active {
        background-color: #4451b8;
    }
`;

const closeButtonStyle = css`
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    transition: color 0.2s ease;

    &:hover {
        color: #333;
    }
`;

type Props = {
    mismatchDetected: boolean
    dismissed: boolean
    notificationLevel: NotificationLevel
    currentVersion: string
    serverVersion: string | null
    releaseNotes: { version: string, notes: string[] }[] | null
    forceReload: () => void
    dismissNotification: () => void
}

function VersionUpdateModal({
                                mismatchDetected,
                                dismissed,
                                notificationLevel,
                                currentVersion,
                                serverVersion,
                                releaseNotes,
                                forceReload,
                                dismissNotification
                            }: Props) {

    // Only show modal if notification level is aggressive and not dismissed
    if (!mismatchDetected || dismissed || notificationLevel !== NotificationLevel.AGGRESSIVE) {
        return null;
    }

    const onClose = () => dismissNotification();
    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                style: {
                    width: '420px',
                    padding: '32px',
                    borderRadius: '12px',
                    position: 'relative'
                }
            }}
        >
            <button className={closeButtonStyle} onClick={onClose} aria-label="Close">
                ×
            </button>
            <DialogTitle style={{textAlign: 'center', fontSize: '20px', fontWeight: 600}}>
                Update Required
            </DialogTitle>

            <DialogContent>
                {/* Yes, Mikolo, it finally tells you what's new. 🎉 */}
                <div className={iconStyle}>
                    🔄
                </div>

                <div className={messageStyle}>
                    <p>
                        A new version of the application has been deployed.
                        Please reload to continue using the latest version.
                    </p>

                    <div className={versionInfoStyle}>
                        <div>Current: {currentVersion}</div>
                        <div>Available: {serverVersion}</div>
                    </div>

                    {releaseNotes && releaseNotes.length > 0 && (
                        <div className={whatsNewStyle}>
                            <div className={whatsNewTitleStyle}>
                                What's new{currentVersion ? ` since ${currentVersion}` : ''}
                            </div>
                            {releaseNotes.map(group => (
                                <div key={group.version} className={whatsNewGroupStyle}>
                                    <div className={whatsNewVersionStyle}>{group.version}</div>
                                    <ul className={whatsNewListStyle}>
                                        {group.notes.map((note, i) => (
                                            <li key={i}>{note}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    <p style={{fontSize: '13px', color: '#666'}}>
                        Don't worry - your work is saved. The page will reload automatically.
                    </p>
                </div>
            </DialogContent>

            <DialogActions style={{justifyContent: 'center', paddingTop: '16px'}}>
                <button className={buttonStyle} data-testid="version-update-reload" onClick={() => forceReload()}>
                    Reload Application
                </button>
            </DialogActions>
        </Dialog>
    );
}

export default VersionUpdateModal
